import React from "react";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Axios, { Canceler } from "axios";
import { Helmet } from "react-helmet";
import { GET_SEO_CONFIG_ACTION } from "./redux/actions/seo";
import { seoSelector } from "./redux/selectors/seo";
import PageLoader from "./components/elements/loader/PageLoader";
import { HOME } from "./_foundation/constants/common";
import * as ROUTES from "./constants/routes";

function isRouteForSeoPages(pathname: string): boolean {
  if (!pathname) return false;
  const allUrlRoutes = Object.values(ROUTES) || [];
  let isSeoPage = true;
  allUrlRoutes.forEach((route) => {
    if (typeof route === "string" && route !== ROUTES.HOME && (pathname.includes(route) || route.includes("/c/"))) {
      isSeoPage = false;
    }
  });
  return isSeoPage;
}

interface Props {
  children: React.ReactNode | React.ReactNode[];
}

// This component is used to load page(s) which will be rendered in SEO.tsx by calling URLs API faster and before for inital load.
// This will also be used to serve lighter DOM to SEO bots on 404 pages.
// seoConfig contains the details of page to be loaded.
const PageResolver: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch();
  const seoConfig = useSelector(seoSelector);

  const pathname = window.location.pathname || "";
  const identifier = useMemo(() => {
    let splitPath: string[];
    if (pathname.endsWith("/")) {
      const newPathname = pathname.slice(0, -1);
      window.history.replaceState(null, "", newPathname);
      splitPath = newPathname.split("/");
    } else {
      splitPath = pathname.split("/");
    }
    const _i = splitPath[splitPath.length - 1] || HOME;
    return decodeURI(_i);
  }, [pathname]);

  const isSeoPage = useMemo(() => isRouteForSeoPages(pathname), [pathname]);
  const isBotCrawling: boolean = window?.["__isPrerender__"] || false;
  const isSeoConfigEmpty: boolean = Object.keys(seoConfig).length === 0; //this being true can presumed as fetching = true
  const isPageNotFound: boolean = seoConfig?.[identifier]?.page?.type === "NotFound";

  const cancels: Canceler[] = [];
  const CancelToken = Axios.CancelToken;
  const payloadBase: any = {
    cancelToken: new CancelToken(function executor(c) {
      cancels.push(c);
    }),
  };

  useEffect(() => {
    if (isSeoPage) {
      dispatch(
        GET_SEO_CONFIG_ACTION({
          identifier,
          storeId: process.env.REACT_APP_STOREID, // storeId param is called by online_store API but for faster results we're using hardcoded value from .env file
          ...payloadBase,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ROLLBACK BECAUSE OF PROD ISSUE ON STATIC PAGES "/c/*"
  // if (isBotCrawling && isSeoConfigEmpty) {
  //   return <PageLoader minHeight="100vh" />;
  // } else if (isBotCrawling && isPageNotFound && isSeoPage) {
  //   return (
  //     <>
  //       <Helmet>
  //         <meta name="prerender-status-code" content="404" />
  //       </Helmet>
  //       <h1>Error 404: Page Not Found</h1>
  //     </>
  //   );
  // } else {
  //   return <>{children}</>;
  // }

  return <>{children}</>;
};

export default PageResolver;
